import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MultiTranslateHttpLoader, MyMissingTranslationHandler } from './helpers/MyMissingTranslationHandler';

import { SharedModule } from './shared/shared.module';

import { JwtInterceptor } from './helpers/jwt.interceptor';
import { ErrorInterceptor } from './helpers/error.interceptor';
import { ActivatedRoute } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';

// import { OfflineInterceptor } from './helpers/offline.interceptor';

import { BuildDetailsHttpService } from './helpers/BuildDetails/build-details.http.service';
import { DaysOffConfigComponent } from './features/Availability/days-off-config/days-off-config.component';
import { DaysOffFormComponent } from './features/Availability/days-off-config/days-off-form/days-off-form.component';
import { HoursConfigComponent } from './features/Availability/hours-config/hours-config.component';
import { HoursFormComponent } from './features/Availability/hours-config/hours-form/hours-form.component';
import { CancellationComponent } from './features/Cancellation & Refund Policies/cancellation/cancellation.component';
import { CancellationFormComponent } from './features/Cancellation & Refund Policies/cancellation/components/cancellation-form/cancellation-form.component';
import { ExceptionRuleGuestsTagsComponent } from './features/Cancellation & Refund Policies/cancellation/components/exception-rule-guests-tags/exception-rule-guests-tags.component';
import { ExceptionRuleReservationComponent } from './features/Cancellation & Refund Policies/cancellation/components/exception-rule-reservation/exception-rule-reservation.component';
import { ShowPolicyComponent } from './features/Cancellation & Refund Policies/cancellation/components/show-policy/show-policy.component';
import { EventFormComponent } from './features/Events-and-guests/events/event-form/event-form.component';
import { EventsComponent } from './features/Events-and-guests/events/events.component';
import { GuestAnnouncementsComponent } from './features/Events-and-guests/guest-announcements/guest-announcements.component';
import { GuestReviewDetailsComponent } from './features/Events-and-guests/guests-reviews/guest-review-details/guest-review-details.component';
import { GuestsReviewsComponent } from './features/Events-and-guests/guests-reviews/guests-reviews.component';
import { ReportReviewFormComponent } from './features/Events-and-guests/guests-reviews/report-review-form/report-review-form.component';
import { FloorFormComponent } from './features/Floors-and-Kitchens/floors-management/floor-form/floor-form.component';
import { FloorsManagementComponent } from './features/Floors-and-Kitchens/floors-management/floors-management.component';
import { KitchenFormComponent } from './features/Floors-and-Kitchens/kitchen-management/kitchen-form/kitchen-form.component';
import { KitchenManagementComponent } from './features/Floors-and-Kitchens/kitchen-management/kitchen-management.component';
import { ListComponent } from './features/Floors-and-Kitchens/list/list.component';
import { AssignServerComponent } from './features/Floors-and-Kitchens/servers-assignment/assign-server/assign-server.component';
import { ServersAssignmentComponent } from './features/Floors-and-Kitchens/servers-assignment/servers-assignment.component';
import { IngredientsStockComponent } from './features/Stock Management/ingredients-stock/ingredients-stock.component';
import { StockTransactionComponent } from './features/Stock Management/stock-transaction/stock-transaction.component';
import { ProfileManagementComponent } from './features/auth/profile-management/profile-management.component';
import { ErrorComponent } from './features/pages/error/error.component';
import { NotAuthorizedComponent } from './features/pages/not-authorized/not-authorized.component';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { HeaderComponent } from './layouts/admin-layout/header/header.component';
import { SidebarComponent } from './layouts/admin-layout/sidebar/sidebar.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { EmptyLayoutComponent } from './layouts/empty-layout/empty-layout.component';
import { AnonymousLayoutComponent } from './layouts/anonymous-layout/anonymous-layout.component';
import { ActionsBarComponent } from './layouts/admin-layout/actions-bar/actions-bar.component';
import { TillAmountConfirmComponent } from './layouts/admin-layout/actions-bar/components/till-amount-confirm/till-amount-confirm.component';
import { TranslationLoadedService } from './services/TranslationLoadedService';
export function fetchBuildDetails(buildDetailsService: BuildDetailsHttpService) {
  return () => buildDetailsService.fetchBuildDetails();
}
export function HttpLoaderFactory(httpClient: HttpClient, translationLoaded: TranslationLoadedService): TranslateLoader {
  return new MultiTranslateHttpLoader(httpClient, translationLoaded);
  // return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}
export function initializeTranslations(translationLoadedService: TranslationLoadedService): () => Promise<void> {
  return async (): Promise<void> => {
    return new Promise(resolve => {
      translationLoadedService.loaded$().subscribe(() => resolve());
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,

    BasicLayoutComponent,
    AnonymousLayoutComponent,
    EmptyLayoutComponent,
    AdminLayoutComponent,
    HeaderComponent,
    SidebarComponent,
    ActionsBarComponent,
    ErrorComponent,
    KitchenManagementComponent,
    KitchenFormComponent,
    FloorsManagementComponent,
    FloorFormComponent,
    EventsComponent,
    GuestAnnouncementsComponent,
    EventFormComponent,
    GuestsReviewsComponent,
    HoursConfigComponent,
    DaysOffConfigComponent,
    DaysOffFormComponent,
    HoursFormComponent,
    ProfileManagementComponent,
    CancellationComponent,
    CancellationFormComponent,
    ShowPolicyComponent,
    ExceptionRuleReservationComponent,
    ExceptionRuleGuestsTagsComponent,
    GuestReviewDetailsComponent,
    StockTransactionComponent,
    IngredientsStockComponent,
    ListComponent,
    ServersAssignmentComponent,
    AssignServerComponent,
    NotAuthorizedComponent,
    ReportReviewFormComponent,
    TillAmountConfirmComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    SharedModule,
    NgSelectModule,
    TranslateModule.forRoot({
      defaultLanguage: localStorage.getItem('language') || 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (HttpLoaderFactory),
        deps: [HttpClient, TranslationLoadedService]
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
    })
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initializeTranslations, deps: [TranslationLoadedService], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    // { provide: APP_INITIALIZER, useFactory: fetchBuildDetails, deps: [BuildDetailsHttpService], multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    private activatedRoute: ActivatedRoute,
    public translate: TranslateService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['lang'] && (params['lang'] == 'ar' || 'en')) {
        if (localStorage.getItem('language') !== params['lang']) {
          localStorage.setItem('language', params['lang']);
          translate.use(params['lang']);
          location.reload();
        }
      }
    });
  }
}
